import { lazy } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { watchSeedRejection } from "@/features/watch/records";
import { useGameContext } from "../frame/use-game-context";
import { GameQuickNav } from "../frame/game-quick-nav";
import { gameIndexQueryOptions } from "../game.queries";
import SugarscapeSurface from "../sugarscape/surface";
import type { GameIndexPage } from "../game-index-data";
import { gamePageSearch } from "./game-page-search";

const GameHome = lazy(() => import("./game-home"));
const SugarscapePage = lazy(() => import("../sugarscape/landing"));
const WatchTheater = lazy(() =>
  import("@/features/watch").then((module) => ({
    default: module.WatchTheater,
  })),
);
const CreatorUpload = lazy(() =>
  import("@/features/coworlds/upload").then((module) => ({
    default: module.CreatorUpload,
  })),
);
const DeskPage = lazy(() =>
  import("@/features/desk").then((module) => ({
    default: module.DeskPage,
  })),
);

export function GameIndexContent({
  page,
  updatedAt,
  parameters,
}: {
  readonly page: GameIndexPage;
  readonly updatedAt: number;
  readonly parameters: URLSearchParams;
}) {
  const game = useGameContext();
  const client = useQueryClient();
  const options = gameIndexQueryOptions(game.publicPath);
  const search = gamePageSearch(parameters);

  switch (page.kind) {
    case "upload":
      return <CreatorUpload data={page.data} />;
    case "sugarscape":
      return (
        <SugarscapeSurface>
          <SugarscapePage game={game.address} />
        </SugarscapeSurface>
      );
    case "home":
      return <GameHome data={page.data} search={search} />;
    case "desk":
      return (
        <DeskPage
          context={page.game}
          initialFeed={{ page: page.pitches, updatedAt: updatedAt }}
        />
      );
    default:
      return (
        <>
          <GameQuickNav />
          <WatchTheater
            key={page.data.canonical}
            seed={page.data.seed}
            onSeedRejected={watchSeedRejection({
              client,
              queryKey: options.queryKey,
              data: page,
            })}
          />
        </>
      );
  }
}
